import React, { useEffect, useState, createContext, useContext } from 'react';
export type PromotionType = 'percent' | 'fixed';
export type Promotion = {
  id: string;
  code: string;
  title: string;
  description?: string;
  type: PromotionType;
  value: number;
  minSubtotal?: number;
  active: boolean;
  expiresAt?: number;
};
export type ValidationResult = {
  ok: boolean;
  promo?: Promotion;
  reason?: string;
};
type Ctx = {
  promotions: Promotion[];
  addPromotion: (promo: Omit<Promotion, 'id'>) => void;
  updatePromotion: (id: string, patch: Partial<Promotion>) => void;
  deletePromotion: (id: string) => void;
  validate: (code: string, subtotal: number) => ValidationResult;
  computeDiscount: (promo: Promotion, subtotal: number) => number;
};
const PromotionsContext = createContext<Ctx | null>(null);
const STORAGE_KEY = 'mp_promotions';
const seed: Promotion[] = [{
  id: 'p_1',
  code: 'WELCOME10',
  title: '10% Off Welcome',
  description: 'Get 10% off your first order over ETB 100',
  type: 'percent',
  value: 10,
  minSubtotal: 100,
  active: true
}, {
  id: 'p_2',
  code: 'SAVE20',
  title: 'ETB 20 Off',
  description: 'Save ETB 20 on orders over ETB 150',
  type: 'fixed',
  value: 20,
  minSubtotal: 150,
  active: true
}, {
  id: 'p_3',
  code: 'EXPIRED50',
  title: '50% Off Flash Sale',
  type: 'percent',
  value: 50,
  active: true,
  expiresAt: Date.now() - 86400000 // Expired yesterday
}];
function loadInitial(): Promotion[] {
  if (typeof window === 'undefined') return seed;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return seed;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : seed;
  } catch {
    return seed;
  }
}
export function PromotionsProvider({
  children
}: {
  children: ReactNode;
}) {
  const [promotions, setPromotions] = useState<Promotion[]>(loadInitial);
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(promotions));
    } catch {
      /* ignore */
    }
  }, [promotions]);
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY || e.newValue == null) return;
      try {
        const parsed = JSON.parse(e.newValue);
        if (Array.isArray(parsed)) setPromotions(parsed);
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  const addPromotion = (promo: Omit<Promotion, 'id'>) => {
    const newPromo: Promotion = {
      ...promo,
      id: `p_${Math.random().toString(36).slice(2, 9)}`,
      code: promo.code.toUpperCase()
    };
    setPromotions(prev => [newPromo, ...prev]);
  };
  const updatePromotion = (id: string, patch: Partial<Promotion>) => {
    setPromotions(prev => prev.map(p => p.id === id ? {
      ...p,
      ...patch,
      code: patch.code ? patch.code.toUpperCase() : p.code
    } : p));
  };
  const deletePromotion = (id: string) => {
    setPromotions(prev => prev.filter(p => p.id !== id));
  };
  const validate = (code: string, subtotal: number): ValidationResult => {
    const upperCode = code.trim().toUpperCase();
    const promo = promotions.find(p => p.code === upperCode);
    if (!promo) return {
      ok: false,
      reason: 'Invalid promo code.'
    };
    if (!promo.active) return {
      ok: false,
      reason: 'This promo code is inactive.'
    };
    if (promo.expiresAt && Date.now() > promo.expiresAt) return {
      ok: false,
      reason: 'This promo code has expired.'
    };
    if (promo.minSubtotal && subtotal < promo.minSubtotal) return {
      ok: false,
      reason: `Minimum order of ETB ${promo.minSubtotal} required.`
    };
    return {
      ok: true,
      promo
    };
  };
  const computeDiscount = (promo: Promotion, subtotal: number): number => {
    if (promo.type === 'percent') {
      return subtotal * promo.value / 100;
    }
    return Math.min(promo.value, subtotal); // Don't discount more than the subtotal
  };
  return <PromotionsContext.Provider value={{
    promotions,
    addPromotion,
    updatePromotion,
    deletePromotion,
    validate,
    computeDiscount
  }} data-id="element-2817">
      {children}
    </PromotionsContext.Provider>;
}
export function usePromotions() {
  const ctx = useContext(PromotionsContext);
  if (!ctx) throw new Error('usePromotions must be used within PromotionsProvider');
  return ctx;
}